import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActivePatient } from './useActivePatient';

/**
 * For pages scoped to ONE patient's record (a result, an appointment, …): when
 * the active patient is switched while such a page is open, the record no
 * longer belongs to the patient in context. Instead of showing a "not found"
 * state, leave the page and go to `to` (normally the parent list).
 *
 * Only a change from one patient to ANOTHER counts as a switch — the initial
 * selection (nothing → patient) does not redirect. The redirect replaces the
 * history entry so "Back" doesn't land on the now-orphaned detail URL.
 */
export function useExitOnPatientSwitch(to: string): void {
  const navigate = useNavigate();
  const { patient } = useActivePatient();
  const fhirId = patient?.fhirId ?? null;
  const seenRef = useRef<string | null>(fhirId);

  useEffect(() => {
    if (fhirId === null) return;
    const previous = seenRef.current;
    seenRef.current = fhirId;
    if (previous !== null && previous !== fhirId) {
      navigate(to, { replace: true });
    }
  }, [fhirId, to, navigate]);
}
